import { SaveOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col,
  message, Row,
  Spin,
  Typography
} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import {
  Controller, FormProvider,
  useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import ManagementInfo from 'common/components/ManagementInfo';
import { createCustomerService, getCustomerService, updateCustomerService } from 'common/services/extends/customer';
import { CreateCustomerDataTypes, UpdateCustomerDataTypes } from 'common/services/extends/customer/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { customerSchema } from 'common/utils/schemas';

type CustomerFormTypes = {
  name: string
  primaryEmail: string
  phone: string,
  client: OptionType
};

const DEFAULT_VALUES: CustomerFormTypes = {
  name: '',
  primaryEmail: '',
  phone: '',
  client: {
    label: ''
  }
};

const CustomerDetail: React.FC<ActiveRoles> = ({
  roleIndex,
  roleUpdate
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();

  const idParams = searchParams.get('id');
  const method = useForm<CustomerFormTypes>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(customerSchema),
    mode: 'onChange'
  });

  const queryCustomerDataById = ['getCustomerDataById', idParams];

  const { data: customerData, isLoading: customerDataLoading } = useQuery(
    queryCustomerDataById,
    () => {
      if (idParams) {
        return getCustomerService(idParams);
      }
      return undefined;
    },
    {
      enabled: roleIndex && !!idParams,
      onSuccess: (data) => {
        if (data) {
          method.reset({
            name: data.customerData.name || '',
            primaryEmail: data.customerData.primaryEmail || '',
            phone: data.customerData.phone || '',
            client: {
              label: data.customerData.clientData?.name || '',
              value: data.customerData.clientData?.id || ''
            }
          });
        } else {
          method.reset(DEFAULT_VALUES);
        }
      },
    }
  );

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    ['createCustomerMutate'],
    async (params: CreateCustomerDataTypes) => createCustomerService(params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(ROUTE_PATHS.CUSTOMER_MANAGEMENT);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            method.setError(error.field as keyof CustomerFormTypes, { message: error.message || '' });
          });
        } else {
          message.error(t('message.createError'));
        }
      },
    }
  );

  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    ['customer-update'],
    async (params: UpdateCustomerDataTypes) => updateCustomerService(params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        queryClient.invalidateQueries(queryCustomerDataById);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          if (errors[0].code === 'active') {
            message.error(errors[0].message);
          } else {
            errors.forEach((error: ErrorResponse) => {
              method.setError(error.field as keyof CustomerFormTypes, { message: error.message || '' });
            });
          }
        } else {
          message.error(t('message.updateError'));
        }
      },
    }
  );

  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }

    const formData = method.getValues();
    const dataRequest: CreateCustomerDataTypes = {
      name: formData.name || '',
      primaryEmail: formData.primaryEmail || '',
      phone: formData.phone || '',
      clientId: Number(formData.client.value),
    };

    if (idParams) {
      updateMutate({
        id: idParams,
        ...dataRequest
      });
    } else {
      createMutate(dataRequest);
    }
  };

  return (
    <FormProvider<CustomerFormTypes> {...method}>
      <HeaderPage
        fixed
        title={idParams ? 'Edit' : 'Create'}
        rightHeader={(
          <Button
            type="primary"
            loading={customerDataLoading || updateLoading}
            onClick={method.handleSubmit(onSubmit)}
            disabled={!roleUpdate}
          >
            <SaveOutlined />
            {t('system.save')}
          </Button>
        )}
      />
      <Spin spinning={customerDataLoading || createLoading || updateLoading}>
        <div className="t-mainlayout_wrapper">
          <Row gutter={16}>
            <Col xxl={18} xl={16} lg={16}>
              <Card>
                <Row gutter={16}>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>
                      {t('system.name')}
                      {' '}
                    </Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="name"
                      render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            name="name"
                            placeholder={`${t('system.input')} ${t('system.name')}`}
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                          />
                        )}
                    />
                  </Col>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>
                      {t('system.email')}
                      {' '}
                    </Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="primaryEmail"
                      render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            name="primaryEmail"
                            placeholder={`${t('system.input')} ${t('system.email')}`}
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                          />
                        )}
                    />
                  </Col>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>
                      {t('system.phone')}
                      {' '}
                    </Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="phone"
                      render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            name="phone"
                            placeholder={`${t('system.input')} ${t('system.phone')}`}
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                          />
                        )}
                    />
                  </Col>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>
                      {t('sidebar.client')}
                      {' '}
                    </Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="client"
                      render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <>
                          <DropdownElement
                            type="client"
                            placeholder={`${t('system.select')} ${t('sidebar.client')}`}
                            locale="vi"
                            value={value}
                            onChange={onChange}
                            isGetOption
                            size="large"
                          />
                          {error && <span className="a-input_errorMessage">{(error as any).value.message}</span>}
                        </>
                      )}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xxl={6} xl={8} lg={8}>
              <ManagementInfo
                createdDate={customerData?.customerData?.createdAt ? dayjs(customerData?.customerData?.createdAt).fromNow() : ''}
                createdBy={customerData?.creator?.name || ''}
                lastUpdated={customerData?.customerData?.updatedAt ? dayjs(customerData?.customerData?.updatedAt).fromNow() : ''}
                lastUpdatedBy={customerData?.updater?.name || ''}
              />
            </Col>
          </Row>
        </div>
      </Spin>
    </FormProvider>
  );
};

export default CustomerDetail;
